import type { PayloadAction } from '@reduxjs/toolkit';
import type { UnitedCanvasReducerState } from './unitedCanvasReducer';
import type { RootState } from '..';
import type { FunnelMapStickyNote } from '../../types/FunnelMapStickyNote';
import type { FreePosition } from '../../types/FreePosition';

export const stickyNotesReducer = {
  addStickyNoteToCanvas: (
    state: UnitedCanvasReducerState,
    action: PayloadAction<{
      stickyNote: FunnelMapStickyNote;
    }>,
  ) => {
    if (!state.funnelMap) {
      return;
    }

    const { stickyNote } = action.payload;

    // Initialize sticky notes if they don't exist
    if (!state.funnelMap.stickyNotes) {
      state.funnelMap.stickyNotes = {};
    }

    state.funnelMap.stickyNotes[stickyNote.stickyNoteId] = stickyNote;
  },

  moveStickyNoteOnCanvas: (
    state: UnitedCanvasReducerState,
    action: PayloadAction<{
      stickyNoteId: string;
      newPosition: FreePosition;
      sectionId: string | null;
    }>,
  ) => {
    if (!state.funnelMap?.stickyNotes) {
      return;
    }

    const { stickyNoteId, newPosition, sectionId } = action.payload;
    const stickyNote = state.funnelMap.stickyNotes[stickyNoteId];

    if (stickyNote) {
      stickyNote.xyPosition = newPosition;
      stickyNote.sectionId = sectionId ?? undefined;
    }
  },

  resizeStickyNoteOnCanvas: (
    state: UnitedCanvasReducerState,
    action: PayloadAction<{
      stickyNoteId: string;
      width: number;
      height: number;
      xyPosition: FreePosition;
      textScale: number; // Float
    }>,
  ) => {
    if (!state.funnelMap?.stickyNotes) {
      return;
    }

    const { stickyNoteId, width, height, xyPosition, textScale } = action.payload;
    const stickyNote = state.funnelMap.stickyNotes[stickyNoteId];

    if (stickyNote) {
      stickyNote.width = width;
      stickyNote.height = height;
      stickyNote.xyPosition = xyPosition;
      stickyNote.textScale = textScale;
    }
  },

  updateStickyNoteData: (
    state: UnitedCanvasReducerState,
    action: PayloadAction<{
      stickyNoteId: string;
      color?: string;
      richText?: string;
      textScale?: number; // Float
      height?: number;
    }>,
  ) => {
    if (!state.funnelMap?.stickyNotes) {
      return;
    }

    const { stickyNoteId, color, richText, textScale, height } = action.payload;
    const stickyNote = state.funnelMap.stickyNotes[stickyNoteId];

    if (stickyNote) {
      if (color !== undefined) {
        stickyNote.backgroundColor = color;
      }
      if (richText !== undefined) {
        stickyNote.richText = richText;
      }
      if (textScale !== undefined) {
        stickyNote.textScale = textScale;
      }

      if (height !== undefined) {
        stickyNote.height = height;
      }
    }
  },

  removeStickyNoteFromCanvas: (
    state: UnitedCanvasReducerState,
    action: PayloadAction<{
      stickyNoteId: string;
    }>,
  ) => {
    if (!state.funnelMap?.stickyNotes) {
      return;
    }

    const { stickyNoteId } = action.payload;
    delete state.funnelMap.stickyNotes[stickyNoteId];
  },
};

// Selectors
export const selectStickyNotes = (state: RootState) =>
  state.unitedCanvas.present.funnelMap?.stickyNotes;

export const selectStickyNoteById = (stickyNoteId: string) => (state: RootState) =>
  state.unitedCanvas.present.funnelMap?.stickyNotes?.[stickyNoteId];
